import {
  GetCustomerLoadsWithRyderNumberQuery,
  GetLatestStopEstimatesQuery,
} from '~/src/components/shared/features/__generated_types__/queries.gql'

import {
  StopAddRequest,
  StopDuplicateRequest,
  StopUpdate,
  StopStatus as GeneratedStopStatus,
  LoadStatus,
} from '~/__generated_types__/globalTypes'
import {
  GetAllCostCentersQuery,
  GetCostCenterFixedRoutesQuery,
  GetCostCenterLocationsQuery,
  GetDriversQuery,
  GetLoadGeorouteQuery,
  GetLoadQuery,
  GetReferenceTypesQuery,
  GetLoadsQuery,
  GetMileageTypesQuery,
  GetOverlappingLegAssignmentsForTractorQuery,
  GetOverlappingTrailerQuery,
  GetStopEventsForCostCenterQuery,
  GetTractorSearchQuery,
  UpdateStopMutation,
  ManualArriveDepartMutationVariables,
  ManualArriveDepartMutation,
  GetEquipmentMoveForTractorQuery,
  GetOverlappingLegAssignmentsForDriverQuery,
  GetTrailerSearchQuery,
  GetCarriersQuery,
  GetReschedulableStopsQuery,
  GetFixedRouteStopsQuery,
  GetCostCenterStopLateReasonsQuery,
} from '~/src/components/Operational/LoadBoardDashboard/__generated_types__/queries.gql'

// TODO We should have a types file at the list view level and at the detail view level
// each with a `Stop` type so that we don't have multiple stop types defined in
// one file
// Ticket: https://app.asana.com/0/1209113321108172/1209561513899793
export type Load = GetLoadsQuery['getLoads']['results'][0]
export type Leg = Load['legs'][0]

export type ListViewStop = Leg['stops'][0]
export type DetailedLoad = Omit<GetLoadQuery['getLoad'], 'legs'> & {
  legs: Array<LegWithEstimates>
}
export type FreightRecord = DetailedLoad['freightDetails']['freightRecords'][number]
export type OsdRecord = Exclude<FreightRecord['osdRecord'], null | undefined>
export type LoadWithScheduleEstimates = GetLatestStopEstimatesQuery['getLoad']
export type StopWithEstimate = LoadWithScheduleEstimates['legs'][0]['stops'][0]
export type UpdateStop = Extract<
  UpdateStopMutation['updateStop'],
  { __typename: 'Load' }
>
export type LegWithEstimates = Omit<GetLoadQuery['getLoad']['legs'][0], 'stops'> & {
  stops: Array<Stop>
}
export type Stop = GetLoadQuery['getLoad']['legs'][0]['stops'][0] & {
  globalIdx?: number
}
export type DetailedLoadCore = Omit<DetailedLoad, 'initialEstimatedTime' | 'legs'> & {
  legs: Array<
    Omit<
      LegWithEstimates,
      | 'plannedMilesPcmiler'
      | 'estimatedTripTime'
      | 'currentTotalEstimatedMiles'
      | 'stops'
    > & {
      stops: Array<Omit<Stop, 'plannedMiles' | 'estimateData'>>
    }
  >
}
export type StopEstimate = Stop['estimateData']
export type CostCenterWithCode = GetAllCostCentersQuery['getAllActiveCostCenters'][0]
export type DetailViewCostCenter = DetailedLoad['costCenter']
export type StopLateReason =
  GetCostCenterStopLateReasonsQuery['getCostCenters']['results'][0]['stopLateReasons'][0]
export type LoadCostCenter = NonNullable<GetLoadQuery['getLoad']['costCenter']>
export type Driver = GetDriversQuery['getDrivers']['results'][0]
export type StopLocation = GetCostCenterLocationsQuery['getLocations']['results'][0]
export type MileageType = GetMileageTypesQuery['getCostCenterMileageTypes'][0]
export type StopType =
  GetStopEventsForCostCenterQuery['getCostCenters']['results'][0]['stopTypes'][0]
export type Route =
  GetCostCenterFixedRoutesQuery['getCostCenters']['results'][0]['fixedRoutes'][0]
export type RouteStops = GetFixedRouteStopsQuery['getRoutes']['results'][0]
export type RouteStop = RouteStops['stops'][0]
export type CostCenterWithRoutes =
  GetCostCenterFixedRoutesQuery['getCostCenters']['results'][0]
export type StopActionWithName =
  GetStopEventsForCostCenterQuery['getCostCenters']['results'][0]['allowedStopEvents'][0]
export type LoadReference = Omit<
  GetLoadQuery['getLoad']['customerReferenceNumbers'][0],
  '__typename' | 'loadId'
> & {
  loadId?: number | null
  displayName?: string | null
  stopId?: bigint | null
  freightRecordId?: number | null
}

export type Tractor = GetTractorSearchQuery['getTractors']['results'][number]
export type Trailer = GetTrailerSearchQuery['getTrailers']['results'][number]
export type OverlappingLoadsForTractor =
  GetOverlappingLegAssignmentsForTractorQuery['getTractors']['results'][number]['overlappingLoadAssignments']
export type OverlappingLoadsForTrailer =
  GetOverlappingTrailerQuery['getTrailers']['results'][number]['overlappingLoadAssignments']
export type OverlappingLoadsForDriver = Extract<
  GetOverlappingLegAssignmentsForDriverQuery['getDriversWithStatus'],
  { __typename?: 'DriverWithStatusPaginatedResponse' }
>['results'][0]['currentStatus']['overlappingLoadAssignmentsForDriver']

export type EquipmentMove =
  GetEquipmentMoveForTractorQuery['getLoad']['legs'][0]['equipmentMoveRequired']

export type LoadMetaData = {
  currentPage: number
  totalPages: number
  totalRecords: number
}

export type DateRange = {
  start: string
  end: string
}

export type TrackingData = {
  legIdx: number
}

export type TrackingDataRescheduleStop = {
  originalRyderNumber: string
  originalStopSequence: number
  originalStopStatus: GeneratedStopStatus | undefined | null
  targetRyderNumber?: string
  entryPoint?: string
  loadStatus?: LoadStatus
}

// TODO: Use the shared version of this enum and remove this one.
export enum StopStatus {
  Completed = 'CMP',
  AtLocation = 'LOC',
  InTransit = 'TRN',
  Planned = 'PLN',
  Cancelled = 'CAN',
  Skipped = 'SKP',
  Attempted = 'ATM',
}

export enum FreightStatus {
  PLANNED = 'Planned',
  PICKED_UP = 'Picked up',
  DELIVERED = 'Delivered',
  OVER = 'Over',
  SHORT = 'Short',
  DAMAGED = 'Damaged',
  CANCELLED = 'Cancelled',
}

export enum ExceptionType {
  OVERAGE = 'Overage',
  SHORTAGE = 'Shortage',
  DAMAGE = 'Damage',
  SKIPPED_STOP = 'Skipped Stop',
  LATE_ARRIVAL = 'Late Arrival',
}

export type ReOrderableStop = Stop & { legId: number }

export enum FieldType {
  CostCenter = 'COST_CENTER',
  FixedRoute = 'FIXED_ROUTE',
}

export type PDFPreview = {
  url: string
  title: string
}

export type StopUpdateBase = Omit<StopUpdate, 'tmwVersionId'>
export type StopBase = Omit<StopAddRequest, 'tmwVersionId' | 'idx' | 'legId'>
export type StopAddBase = Omit<StopAddRequest, 'tmwVersionId'>
export type StopDuplicateBase = Omit<StopDuplicateRequest, 'tmwVersionId'>
export type ReferenceType = GetReferenceTypesQuery['getReferenceTypes'][0]
export type GeorouteLeg = GetLoadGeorouteQuery['getLoad']['legs'][0]

export type ManualArriveDepartStops = ManualArriveDepartMutationVariables['input']
export type ManualArriveDepartInvalidStops =
  ManualArriveDepartMutation['manualArriveDepart']
export type Carrier = GetCarriersQuery['getCarriers'][number]

export type RyderNumber =
  GetCustomerLoadsWithRyderNumberQuery['getLoads']['results'][number]

export type ReschedulableStop = GetReschedulableStopsQuery['getReschedulableStops'][0]

export type LegForAutoDispatch = Pick<Leg, 'idx' | 'plannedStartTime'> & {
  primaryDriver?: Pick<Driver, 'id'> | null
}
export type LoadForAutoDispatch = Pick<
  Load,
  'status' | 'tenderStatus' | 'targetDispatchStartInterval' | 'dispatchAt'
>
